import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Alert, AlertDescription } from "../components/ui/alert";
import { toast } from "sonner";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  Megaphone,
  Plus,
  ArrowLeft,
  Pin,
  Calendar,
  Clock,
  Mail,
  MessageSquare,
  Edit,
  Trash2,
  Send,
  Eye,
  AlertTriangle,
  CheckCircle,
  Info
} from "lucide-react";

const CATEGORIES = [
  { value: "general", label: "General", icon: <Info className="w-4 h-4" /> },
  { value: "meeting", label: "Meeting", icon: <Calendar className="w-4 h-4" /> },
  { value: "maintenance", label: "Maintenance", icon: <AlertTriangle className="w-4 h-4" /> },
  { value: "emergency", label: "Emergency", icon: <AlertTriangle className="w-4 h-4 text-red-500" /> },
  { value: "event", label: "Event", icon: <Calendar className="w-4 h-4" /> },
  { value: "policy", label: "Policy", icon: <Info className="w-4 h-4" /> },
  { value: "financial", label: "Financial", icon: <Info className="w-4 h-4" /> }
];

const PRIORITIES = [
  { value: "low", label: "Low", color: "bg-gray-500/10 text-gray-600" },
  { value: "normal", label: "Normal", color: "bg-blue-500/10 text-blue-600" },
  { value: "high", label: "High", color: "bg-orange-500/10 text-orange-600" },
  { value: "urgent", label: "Urgent", color: "bg-red-500/10 text-red-600" }
];

const AnnouncementsPage = ({ isBoard = false, embedded = false }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showDrafts, setShowDrafts] = useState(false);
  
  // Create/Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    announcement_id: null,
    title: "",
    content: "",
    category: "general",
    priority: "normal",
    target_roles: ["owner", "board", "admin"],
    send_email: false,
    send_sms: false,
    expires_at: ""
  });

  const fetchAnnouncements = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter && categoryFilter !== "all") params.append("category", categoryFilter);
      if (!isBoard || !showDrafts) params.append("published_only", "true");
      else params.append("published_only", "false");
      
      const response = await axios.get(`${API}/announcements?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data);
    } catch (error) {
      toast.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [token, categoryFilter, showDrafts]);

  const handleCreate = async () => {
    if (!formData.title || !formData.content) {
      toast.error("Title and content are required");
      return;
    }
    
    try {
      if (editMode && formData.announcement_id) {
        // Update
        await axios.put(`${API}/announcements/${formData.announcement_id}`, {
          title: formData.title,
          content: formData.content,
          category: formData.category,
          priority: formData.priority,
          target_roles: formData.target_roles,
          send_email: formData.send_email,
          send_sms: formData.send_sms,
          expires_at: formData.expires_at || null
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Announcement updated");
      } else {
        // Create
        await axios.post(`${API}/announcements`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Announcement created");
      }
      
      setEditOpen(false);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save announcement");
    }
  };

  const handlePublish = async (announcementId) => {
    try {
      await axios.post(`${API}/announcements/${announcementId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Announcement published");
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish announcement");
    }
  };

  const handleDelete = async (announcementId) => {
    if (!confirm("Are you sure you want to delete this announcement?")) return;
    
    try {
      await axios.delete(`${API}/announcements/${announcementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete announcement");
    }
  };

  const handleEdit = (announcement) => {
    setFormData({
      announcement_id: announcement.announcement_id,
      title: announcement.title,
      content: announcement.content,
      category: announcement.category,
      priority: announcement.priority,
      target_roles: announcement.target_roles || ["owner", "board", "admin"],
      send_email: announcement.send_email || false,
      send_sms: announcement.send_sms || false,
      expires_at: announcement.expires_at ? announcement.expires_at.split("T")[0] : ""
    });
    setEditMode(true);
    setEditOpen(true);
  };

  const handlePin = async (announcementId, isPinned) => {
    try {
      await axios.put(`${API}/announcements/${announcementId}`, {
        is_pinned: !isPinned
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(isPinned ? "Unpinned" : "Pinned");
      fetchAnnouncements();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const resetForm = () => {
    setFormData({
      announcement_id: null,
      title: "",
      content: "",
      category: "general",
      priority: "normal",
      target_roles: ["owner", "board", "admin"],
      send_email: false,
      send_sms: false,
      expires_at: ""
    });
    setEditMode(false);
  };

  const getCategoryBadge = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        {cat?.icon}
        {cat?.label || category}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const p = PRIORITIES.find(pr => pr.value === priority);
    return <Badge className={p?.color || ""}>{p?.label || priority}</Badge>;
  };

  const content = (
    <div className={isBoard ? "p-6 lg:p-8 max-w-7xl mx-auto" : "min-h-screen bg-background"}>
      {/* Header - only for non-board */}
      {!isBoard && (
        <header className="border-b bg-card/95 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/owner")} className="text-muted-foreground hover:text-foreground transition-colors duration-200">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <span className="font-serif text-lg font-semibold block">Announcements</span>
                  <span className="text-xs text-muted-foreground">Community updates and notices</span>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Page Header for Board */}
      {isBoard && (
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Megaphone className="w-5 h-5 text-primary" />
            </div>
            <div>
              <span className="font-serif text-lg font-semibold block">Announcements</span>
              <span className="text-xs text-muted-foreground">Community updates and notices</span>
            </div>
          </div>
          <Dialog open={editOpen} onOpenChange={(open) => { setEditOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-announcement-btn" className="shadow-button hover:shadow-button-hover transition-all duration-300">
                <Plus className="w-4 h-4 mr-2" />
                New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-serif">{editMode ? "Edit" : "Create"} Announcement</DialogTitle>
                <DialogDescription>Create an announcement for the community</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Announcement title"
                    className="focus:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Content *</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    placeholder="Announcement content..."
                    rows={5}
                    className="focus:ring-primary/20"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITIES.map(p => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Expires On (optional)</Label>
                    <Input
                      type="date"
                      value={formData.expires_at}
                      onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                    />
                  </div>
                  <div className="space-y-3 pt-2 border-t">
                    <Label className="text-sm font-medium">Notification Options</Label>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">Send Email Notification</span>
                      </div>
                      <Switch 
                        checked={formData.send_email}
                        onCheckedChange={(v) => setFormData({...formData, send_email: v})}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">Send SMS Notification</span>
                      </div>
                      <Switch 
                        checked={formData.send_sms}
                        onCheckedChange={(v) => setFormData({...formData, send_sms: v})}
                      />
                    </div>
                    <Alert>
                      <Info className="w-4 h-4" />
                      <AlertDescription className="text-xs">
                        Notifications will be sent when the announcement is published. Email/SMS services are currently in demo mode.
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreate}>
                    {editMode ? "Save Changes" : "Create Draft"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
        </div>
      )}

      <div className={isBoard ? "" : "max-w-7xl mx-auto px-4 md:px-8 py-8"}>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {isBoard && (
            <div className="flex items-center gap-2">
              <Switch checked={showDrafts} onCheckedChange={setShowDrafts} />
              <Label className="text-sm">Show Drafts</Label>
            </div>
          )}
        </div>

        {/* Announcements List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading announcements...</div>
        ) : announcements.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Megaphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No announcements</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {announcements.map((ann) => (
              <Card 
                key={ann.announcement_id} 
                className={`${ann.is_pinned ? 'border-primary bg-primary/5' : ''} ${!ann.is_published ? 'opacity-70 border-dashed' : ''}`}
                data-testid={`announcement-${ann.announcement_id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        {ann.is_pinned && (
                          <Badge className="bg-primary/10 text-primary">
                            <Pin className="w-3 h-3 mr-1" /> Pinned
                          </Badge>
                        )}
                        {!ann.is_published && (
                          <Badge variant="outline" className="border-dashed">Draft</Badge>
                        )}
                        {getCategoryBadge(ann.category)}
                        {getPriorityBadge(ann.priority)}
                      </div>
                      <h3 className="font-semibold text-lg mb-2">{ann.title}</h3>
                      <p className="text-muted-foreground whitespace-pre-wrap">{ann.content}</p>
                      <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(ann.created_at).toLocaleDateString()}
                        </span>
                        {ann.expires_at && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Expires: {new Date(ann.expires_at).toLocaleDateString()}
                          </span>
                        )}
                        {ann.email_sent_at && (
                          <span className="flex items-center gap-1 text-green-600">
                            <Mail className="w-3 h-3" />
                            Email sent
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {isBoard && (
                      <div className="flex flex-col gap-2">
                        {!ann.is_published && (
                          <Button size="sm" onClick={() => handlePublish(ann.announcement_id)}>
                            <Send className="w-4 h-4 mr-1" />
                            Publish
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => handlePin(ann.announcement_id, ann.is_pinned)}>
                          <Pin className="w-4 h-4 mr-1" />
                          {ann.is_pinned ? "Unpin" : "Pin"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(ann)}>
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        {user?.role === "admin" && (
                          <Button variant="destructive" size="sm" onClick={() => handleDelete(ann.announcement_id)}>
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (isBoard) {
    if (embedded) return content;
    return <AdminSidebar>{content}</AdminSidebar>;  }
  return content;
};

export default AnnouncementsPage;
